import {
  readPortalAccess,
  type PortalAccess,
} from "@/services/jambushrustiAuth";
const SESSION_KEY = "libraryAdminSession";

export interface LibraryAdminUser {
  email: string;
  firstName?: string;
  lastName?: string;
  userType?: string;
}

export interface LibraryAdminSession extends PortalAccess {
  accessToken: string;
  refreshToken?: string;
  user: LibraryAdminUser;
}

type LoginResponse = {
  access?: string;
  refresh?: string;
  data?: { access?: string; refresh?: string };
  user?: {
    email?: string;
    first_name?: string;
    last_name?: string;
    user_type?: string;
  };
  message?: string;
  detail?: string;
  error?: string | { message?: string };
};

let refreshRequest: Promise<string> | null = null;

const getApiBaseUrl = () =>
  import.meta.env.DEV
    ? "/jambushrusti-api"
    : (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");

export function getLibraryAdminSession(): LibraryAdminSession | null {
  try {
    const storedSession = localStorage.getItem(SESSION_KEY);
    const session = storedSession ? JSON.parse(storedSession) : null;
    if (session?.can_access_library === true && session.accessToken) {
      return session;
    }
    localStorage.removeItem(SESSION_KEY);
    return null;
  } catch {
    localStorage.removeItem(SESSION_KEY);
    return null;
  }
}

export function isLibraryAdminAuthenticated(): boolean {
  const session = getLibraryAdminSession();
  if (!session?.accessToken) {
    return false;
  }

  try {
    const encodedPayload = session.accessToken.split(".")[1];
    if (!encodedPayload) {
      return true;
    }

    const normalizedPayload = encodedPayload
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(encodedPayload.length / 4) * 4, "=");
    const payload = JSON.parse(atob(normalizedPayload)) as { exp?: number };

    if (payload.exp && payload.exp * 1000 <= Date.now()) {
      if (session.refreshToken) {
        return true;
      }
      logoutLibraryAdmin();
      return false;
    }
  } catch {
    // Opaque access tokens cannot be inspected client-side; their presence is
    // sufficient until an API request rejects them.
  }

  return true;
}

export async function loginLibraryAdmin(
  email: string,
  password: string
): Promise<LibraryAdminSession> {
  const apiBaseUrl = getApiBaseUrl();
  const tenantId = import.meta.env.VITE_X_TENANTID;

  if (!apiBaseUrl || !tenantId) {
    throw new Error("Library authentication is not configured.");
  }

  const response = await fetch(`${apiBaseUrl}/jambushrusti/auth/login/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Tenant-ID": tenantId,
    },
    body: JSON.stringify({ email, password }),
  }).catch(() => {
    throw new Error(
      "Unable to connect. Check your internet connection and try again."
    );
  });

  const payload = (await response.json().catch(() => ({}))) as LoginResponse;

  if (!response.ok) {
    const apiError =
      payload.detail ||
      payload.message ||
      (typeof payload.error === "string"
        ? payload.error
        : payload.error?.message);
    // This API also returns account errors with HTTP 500, so inspect its
    // message before falling back to a status-based service error.
    if (typeof apiError === "string" && apiError.trim()) {
      if (/user not found/i.test(apiError)) {
        throw new Error("No account found with this email or username.");
      }
      throw new Error(apiError);
    }
    if (response.status >= 500) {
      throw new Error(
        "The login service is temporarily unavailable. Please try again shortly."
      );
    }
    if (response.status === 429) {
      throw new Error(
        "Too many sign-in attempts. Please wait a few minutes and try again."
      );
    }
    throw new Error(
      apiError ||
        "No matching account or incorrect password. Check your login details and try again."
    );
  }

  const accessToken = payload.access || payload.data?.access;
  const refreshToken = payload.refresh || payload.data?.refresh;

  if (!accessToken) {
    throw new Error("The login response did not include an access token.");
  }

  const session: LibraryAdminSession = {
    ...readPortalAccess(payload),
    accessToken,
    refreshToken,
    user: {
      email: payload.user?.email || email,
      firstName: payload.user?.first_name,
      lastName: payload.user?.last_name,
      userType: payload.user?.user_type,
    },
  };

  if (!session.can_access_library) {
    throw new Error(
      "This account does not have Library access. Please sign in to the CMS portal if you have CMS access."
    );
  }
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  return session;
}

export async function refreshLibraryAdminToken(): Promise<string> {
  if (refreshRequest) {
    return refreshRequest;
  }

  refreshRequest = (async () => {
    const apiBaseUrl = getApiBaseUrl();
    const tenantId = import.meta.env.VITE_X_TENANTID;
    const session = getLibraryAdminSession();

    if (!apiBaseUrl || !tenantId || !session?.refreshToken) {
      logoutLibraryAdmin();
      throw new Error("Your session has expired. Please sign in again.");
    }

    const response = await fetch(`${apiBaseUrl}/auth/token-refresh/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Tenant-Id": tenantId,
      },
      body: JSON.stringify({ refresh: session.refreshToken }),
    });

    const payload = (await response.json().catch(() => ({}))) as LoginResponse;
    const accessToken = payload.access || payload.data?.access;

    if (!response.ok || !accessToken) {
      logoutLibraryAdmin();
      const apiError =
        payload.detail ||
        payload.message ||
        (typeof payload.error === "string"
          ? payload.error
          : payload.error?.message);
      throw new Error(
        apiError || "Your session has expired. Please sign in again."
      );
    }

    const updatedSession: LibraryAdminSession = {
      ...session,
      accessToken,
      refreshToken:
        payload.refresh || payload.data?.refresh || session.refreshToken,
    };
    localStorage.setItem(SESSION_KEY, JSON.stringify(updatedSession));
    return accessToken;
  })();

  try {
    return await refreshRequest;
  } finally {
    refreshRequest = null;
  }
}

export async function logoutLibraryAdminFromServer(): Promise<void> {
  const apiBaseUrl = getApiBaseUrl();
  const tenantId = import.meta.env.VITE_X_TENANTID;
  const session = getLibraryAdminSession();

  try {
    if (apiBaseUrl && tenantId && session?.accessToken) {
      await fetch(`${apiBaseUrl}/auth/logout/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.accessToken}`,
          "X-Tenant-ID": tenantId,
        },
        body: JSON.stringify({ refresh: session.refreshToken || "" }),
      });
    }
  } finally {
    logoutLibraryAdmin();
  }
}

export function logoutLibraryAdmin(): void {
  localStorage.removeItem(SESSION_KEY);
}
